import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Menu from "../components/Menu";
import Posts from "../components/Posts";
import Notifs from "../components/Notifs";
import CreateDebate from "../components/AskQuestion";
import CreateTextPost from "../components/CreateTextPost";
import Amis from "../components/Amis";
import Toast from "../components/Toast";
import MessageBox from "../components/MessageBox";
import Chat from "../components/Chat";
import Messenger from "../components/Messenger";
import StudentMap from "../components/StudentMap";
import NotificationPrompt from "../components/NotificationPrompt";
import UserSuggestions from "../components/UserSuggestions";
import DebateOnboarding from "../components/DebateOnboarding";
import styles from './home.module.css';




import socket from '../Utils/socket';
import { API_URL } from '../Utils/api';
import { subscribeUserToPush } from '../Utils/push';
import axios from "axios";
import { useCurrentUser } from "../hooks/useUsers";

import { FaTimes, FaImage, FaCalendar, FaInfo, FaSmile, FaQuestionCircle } from "react-icons/fa";

import ImagePosting from "../components/ImagePosting";

const MODALS = {
  debate: CreateDebate,
  post: CreateTextPost,
  img: ImagePosting
};
const FULLSCREEN = [];

const PUBLISH_CHOICES = [
  { id: 'debate', label: 'Débat', icon: <FaQuestionCircle />, color: '#2563eb', desc: 'Lance un débat public' },
  { id: 'post',   label: 'Texte', icon: <FaSmile />, color: '#059669', desc: 'Partage une simple texte' },
  { id: 'img',   label: 'Photo', icon: <FaImage />, color: '#1d40ecff', desc: 'Partage une photo' }
];



const Home = () => {
  const navigate = useNavigate();

  // userData state has been removed because it was never populated
  const [active, setActive]                   = useState('home');
  const [toast, setToast]                     = useState(null);
  const [refresh, setRefresh]                 = useState(false);
  const [modal, setModal]                     = useState(null);
  const [showPublishChoice, setShowPublishChoice] = useState(false);
  const [chat, setChat]                       = useState(null);
  const [showMessBox, setShowMessBox]         = useState(false);
  const [isMobile, setIsMobile]               = useState(() =>
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 768
  );
  const [isDesktop, setIsDesktop]             = useState(() => window.innerWidth > 1024);
  const [showOnboarding, setShowOnboarding]   = useState(false);

  const token = useMemo(() => localStorage.getItem('token'), []);
  const pp = useMemo(() => localStorage.getItem('pp'), []);
  const userId = useMemo(() => localStorage.getItem('userId'), []);
  const username = useMemo(() => localStorage.getItem('username'), []);

  const { data: currentUser, refetch: refetchUser } = useCurrentUser(token);




  useEffect(() => {
    const check = () => {
      setIsMobile(/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 768);
      setIsDesktop(window.innerWidth > 1024);
    };
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    // Affiche le popup uniquement si l'utilisateur ne l'a pas encore vu
    const hasSeenOnboarding = localStorage.getItem('agora_onboarding_seen');
    if (!hasSeenOnboarding) {
        setShowOnboarding(true);
    }
  }, []);

  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('agora_onboarding_seen', 'true');
  };



  const back      = useCallback(() => { setModal(null); setRefresh(p => !p); }, []);
  const openModal = useCallback((name) => { setShowPublishChoice(false); setModal(name); }, []);
  const openChat  = useCallback((receiver) => {
    if (isMobile) navigate(`/chat/${receiver._id}`);
    else setChat({ receiver });
  }, [isMobile, navigate]);

  const base = useMemo(() => ({
    setActive, active, pp,
    userId, setRefresh, refresh
  }), [active, pp, userId, refresh]);

  const renderActive = useMemo(() => {
    switch (active) {
      case 'notifications': return <Notifs {...base} />;
      case 'messenger':     return <Messenger {...base} setShowMessBox={setShowMessBox} onOpenChat={openChat} showChat={!!chat} />;
      case 'students':      return <StudentMap {...base} username={username} onOpenChat={openChat} />;
      case 'amis':          return <Amis pp={pp} setRefresh={setRefresh} refresh={refresh} />;
      case 'home':          return null; // Déjà rendu dans le layout principal

      default:              return null;
    }
  }, [active, base, chat, isDesktop, refresh, openChat, token, userId]);

  const Modal = modal ? MODALS[modal] : null;

  return (
    <div>


      {/* Onboarding Popup */}
      {showOnboarding && (
        <DebateOnboarding 
          onStartDebate={() => openModal('debate')} 
          onClose={handleCloseOnboarding} 
        />
      )}

      {/* Active modal */}
      {Modal && (
        <div className={styles.modalOverlay} onClick={back}>
          <div
            className={FULLSCREEN.includes(modal) ? styles.modalFullscreen : styles.modalContent}
            onClick={e => e.stopPropagation()}
          >
            <Modal back={back} />
          </div>
        </div>
      )}

      {/* Choice modal */}
      {showPublishChoice && !modal && (
        <div className={styles.modalOverlay} onClick={() => setShowPublishChoice(false)}>
          <div className={styles.choiceModal} onClick={e => e.stopPropagation()}>
            <div className={styles.choiceHeader}>
              <h2 className={styles.choiceTitle}>Quoi de neuf ?</h2>
              <button className={styles.choiceClose} onClick={() => setShowPublishChoice(false)}><FaTimes /></button>
            </div>
            <div className={styles.choiceGrid}>
              {PUBLISH_CHOICES.map(c => (
                <button key={c.id} className={styles.choiceCard} onClick={() => openModal(c.id)}>


                  <div className={styles.choiceIcon} style={{ background: `${c.color}15`, color: c.color }}>{c.icon}</div>
                  <div className={styles.choiceInfo}>
                    <span className={styles.choiceLabel}>{c.label}</span>
                    <span className={styles.choiceDesc}>{c.desc}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chat modal — desktop only */}
      {!isMobile && chat && (
        <Chat receiver={chat.receiver} onClose={() => setChat(null)} />
      )}

      {!modal && (
        <>
          <Header pp={pp} active={active} setActive={setActive} />

          <main>
            <Menu
              pp={pp}
              active={active}
              setActive={setActive}
              onPublish={() => setShowPublishChoice(true)}
              socket={socket}
              setToast={setToast}
            />

            {(isDesktop || active === 'home') && (
              <>
                <div className={styles.postSpace}>
                  <Posts userId={userId} setRefresh={setRefresh} refresh={refresh} />
                </div>
                {/* On Desktop, show it on the right side. On mobile, it's hidden from Home because it's inside the 'Amis' tab. */}
                {isDesktop && active === 'home' && (
                  <UserSuggestions userId={userId} token={token} refresh={refresh} />
                )}
              </>
            )}

            {toast && <Toast message={toast} setToast={setToast} />}
            {renderActive}
            {showMessBox && <MessageBox setShowMessBox={setShowMessBox} />}
          </main>
        </>
      )}
    </div>
  );
};

export default Home;